import { useState, type FormEvent } from "react";
import { propertyTypeLabels } from "./api.js";
import {
  SQFT_PER_ACRE,
  useUpdateInsights,
  type PropertyInsights,
  type UpdateInsightsInput,
} from "./insights-api.js";

/**
 * "About this property" card on the property detail screen — the owner-facing
 * view of the auto-detected Property Insights (lot size, home sqft, mowable
 * lawn area, year built, detected type). Values the owner has corrected are
 * tagged "Edited by you" and locked against future auto-estimation; the Edit
 * form PATCHes only changed fields and can reset a field back to auto (null
 * clears both the value and the lock).
 */

const editableFields = [
  { key: "lotSizeSqft", label: "Lot size (sq ft)", min: 0, max: 100_000_000 },
  { key: "homeSqft", label: "Home size (sq ft)", min: 0, max: 100_000_000 },
  { key: "mowableAreaSqft", label: "Mowable lawn area (sq ft)", min: 0, max: 100_000_000 },
  { key: "yearBuilt", label: "Year built", min: 1600, max: 2100 },
] as const;
type EditableKey = (typeof editableFields)[number]["key"];

const fmt = (n: number) => n.toLocaleString("en-US");

/**
 * "Aug 2023" from the NAIP acquisition datetime; null when absent or
 * unparseable (the caption is simply omitted then). UTC keeps midnight
 * timestamps from rolling back a month in western timezones.
 */
function aerialVintage(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
}

/** Lot size reads best in acres once it stops being a city lot. */
function lotSizeDisplay(insights: PropertyInsights): string {
  const sqft = insights.lotSizeSqft!;
  const acres = insights.lotSizeAcres ?? sqft / SQFT_PER_ACRE;
  if (acres >= 0.5) {
    return `${acres.toLocaleString("en-US", { maximumFractionDigits: 2 })} acres`;
  }
  return `${fmt(sqft)} sq ft`;
}

export function InsightsCard({
  propertyId,
  insights,
}: {
  propertyId: string;
  insights: PropertyInsights | null | undefined;
}) {
  const [editing, setEditing] = useState(false);

  const pending = !insights || insights.status === "pending";
  const lookupFailed = insights?.status === "failed" || insights?.status === "out_of_area";
  const vintage = aerialVintage(insights?.aerialCapturedAt);

  return (
    <section className="border-t border-slate-200 px-4 py-4">
      {insights?.aerialUrl && (
        <figure className="mb-3">
          <img
            src={insights.aerialUrl}
            alt="Aerial view of this property"
            className="w-full rounded-lg"
          />
          {vintage && (
            <figcaption className="mt-1 text-xs text-slate-500">
              Aerial imagery: USDA NAIP, {vintage}
            </figcaption>
          )}
        </figure>
      )}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900">About this property</h2>
        {!pending && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex min-h-[36px] items-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700"
          >
            Edit
          </button>
        )}
      </div>
      <p className="mt-0.5 text-xs text-slate-500">Auto-detected from public records</p>

      {pending && (
        <p className="mt-3 rounded-lg border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">
          Property details are being looked up automatically…
        </p>
      )}

      {!pending && insights && editing && (
        <InsightsEditForm
          propertyId={propertyId}
          insights={insights}
          onClose={() => setEditing(false)}
        />
      )}

      {!pending && insights && !editing && (
        <>
          {lookupFailed && (
            <p className="mt-3 rounded bg-slate-50 p-3 text-sm text-slate-600">
              We couldn&apos;t determine property details automatically. Tap Edit to add them
              yourself.
            </p>
          )}
          <InsightsRows insights={insights} />
        </>
      )}
    </section>
  );
}

interface InsightRow {
  key: string;
  label: string;
  value: string;
  hint?: string;
  edited: boolean;
}

function InsightsRows({ insights }: { insights: PropertyInsights }) {
  const edited = (key: string) => insights.overriddenFields?.[key] === true;

  const rows: InsightRow[] = [];
  if (insights.lotSizeSqft !== undefined) {
    rows.push({
      key: "lotSizeSqft",
      label: "Lot size",
      value: lotSizeDisplay(insights),
      edited: edited("lotSizeSqft"),
    });
  }
  if (insights.homeSqft !== undefined) {
    rows.push({
      key: "homeSqft",
      label: "Home size",
      value: `${fmt(insights.homeSqft)} sq ft`,
      edited: edited("homeSqft"),
    });
  }
  if (insights.mowableAreaSqft !== undefined) {
    rows.push({
      key: "mowableAreaSqft",
      label: "Mowable lawn area",
      value: `~${fmt(insights.mowableAreaSqft)} sq ft`,
      hint: insights.mowableAreaBand
        ? `roughly ${fmt(insights.mowableAreaBand.low)}–${fmt(insights.mowableAreaBand.high)} sq ft`
        : undefined,
      edited: edited("mowableAreaSqft"),
    });
  }
  if (insights.yearBuilt !== undefined) {
    rows.push({
      key: "yearBuilt",
      label: "Year built",
      value: String(insights.yearBuilt),
      edited: edited("yearBuilt"),
    });
  }
  if (insights.derivedPropertyType !== undefined) {
    rows.push({
      key: "derivedPropertyType",
      label: "Detected property type",
      value: propertyTypeLabels[insights.derivedPropertyType] ?? insights.derivedPropertyType,
      hint: insights.typeMismatch
        ? "This differs from your selection — we keep your choice."
        : undefined,
      edited: false,
    });
  }

  if (rows.length === 0) return null;

  return (
    <dl className="mt-3 space-y-3 text-sm">
      {rows.map((r) => (
        <div key={r.key}>
          <dt className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
            {r.label}
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal ${
                r.edited ? "bg-teal-50 text-teal-800" : "bg-slate-100 text-slate-500"
              }`}
            >
              {r.edited ? "Edited by you" : "Auto-detected"}
            </span>
          </dt>
          <dd className="mt-0.5 text-slate-900">
            {r.value}
            {r.hint && <span className="block text-xs text-slate-500">{r.hint}</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function InsightsEditForm({
  propertyId,
  insights,
  onClose,
}: {
  propertyId: string;
  insights: PropertyInsights;
  onClose: () => void;
}) {
  const updateMutation = useUpdateInsights(propertyId);
  const [values, setValues] = useState<Record<EditableKey, string>>(() => ({
    lotSizeSqft: insights.lotSizeSqft?.toString() ?? "",
    homeSqft: insights.homeSqft?.toString() ?? "",
    mowableAreaSqft: insights.mowableAreaSqft?.toString() ?? "",
    yearBuilt: insights.yearBuilt?.toString() ?? "",
  }));
  const [resets, setResets] = useState<Record<EditableKey, boolean>>({
    lotSizeSqft: false,
    homeSqft: false,
    mowableAreaSqft: false,
    yearBuilt: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<EditableKey, string>>>({});

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const patch: UpdateInsightsInput = {};
    const nextFieldErrors: Partial<Record<EditableKey, string>> = {};
    for (const f of editableFields) {
      const current = insights[f.key];
      if (resets[f.key]) {
        // null clears BOTH the value and the owner lock, so the next
        // enrichment run may fill the field again.
        patch[f.key] = null;
        continue;
      }
      const raw = values[f.key].trim();
      if (raw === "") continue; // left blank — don't touch the field
      const num = Number(raw.replaceAll(",", ""));
      if (!Number.isInteger(num) || num < f.min || num > f.max) {
        nextFieldErrors[f.key] =
          f.key === "yearBuilt"
            ? "Enter a year between 1600 and 2100"
            : "Enter a whole number of square feet";
        continue;
      }
      if (num !== current) patch[f.key] = num;
    }

    setFieldErrors(nextFieldErrors);
    if (Object.keys(nextFieldErrors).length > 0) return;
    if (Object.keys(patch).length === 0) {
      // Nothing changed — the API rejects an empty PATCH, so just close.
      onClose();
      return;
    }

    try {
      await updateMutation.mutateAsync(patch);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save property details");
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-3 space-y-4" noValidate>
      {error && <div className="rounded bg-rose-50 p-3 text-sm text-rose-900">{error}</div>}
      <p className="text-sm text-slate-500">
        Values you enter replace the auto-detected ones and won&apos;t be overwritten.
      </p>

      {editableFields.map((f) => {
        const overridden = insights.overriddenFields?.[f.key] === true;
        const resetting = resets[f.key];
        return (
          <label key={f.key} htmlFor={`insights-${f.key}`} className="block">
            <span className="mb-1 flex items-center justify-between text-sm font-medium text-slate-700">
              {f.label}
              {overridden && (
                <button
                  type="button"
                  onClick={() => setResets((prev) => ({ ...prev, [f.key]: !prev[f.key] }))}
                  className="text-xs font-medium text-teal-700"
                >
                  {resetting ? "Keep my value" : "Reset to auto"}
                </button>
              )}
            </span>
            <input
              id={`insights-${f.key}`}
              inputMode="numeric"
              disabled={resetting}
              value={values[f.key]}
              onChange={(e) => {
                const next = e.target.value;
                setValues((prev) => ({ ...prev, [f.key]: next }));
                if (fieldErrors[f.key]) {
                  setFieldErrors((prev) => ({ ...prev, [f.key]: undefined }));
                }
              }}
              aria-invalid={fieldErrors[f.key] ? true : undefined}
              className={inputClass}
              placeholder="Not set"
            />
            {resetting && (
              <span className="mt-1 block text-xs text-slate-500">
                Will go back to auto-detect when you save.
              </span>
            )}
            {fieldErrors[f.key] && (
              <span className="mt-1 block text-sm text-rose-700" role="alert">
                {fieldErrors[f.key]}
              </span>
            )}
          </label>
        );
      })}

      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="min-h-[44px] flex-1 rounded-md border border-slate-300 bg-white text-sm font-medium text-slate-700"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={updateMutation.isPending}
          className="min-h-[44px] flex-1 rounded-md bg-teal-700 text-sm font-medium text-white disabled:opacity-60"
        >
          {updateMutation.isPending ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}

const inputClass =
  "block w-full min-h-[44px] rounded-md border border-slate-300 px-3 text-base focus:border-teal-700 focus:outline-none focus:ring-1 focus:ring-teal-700 disabled:bg-slate-50 disabled:text-slate-400";
